/**
 * Base Junto Theme - Foundation for all Junto frontend components
 * This theme provides the default design tokens that can be customized
 * by individual studios while maintaining consistency.
 */

export const baseJuntoTheme = {
  colors: {
    primary: {
      // Main background and text colors used throughout the app
      background: '#FFFFFF', // Default white background
      text: '#2D3748',       // Default dark text
      buttons: '#2B6CB0',    // Default button color
      ui_dark: '#2D3748',    // Default dark UI elements
    },
    accent: {
      // Accent colors for interactive elements and highlights
      main: '#E53E3E',       // Default red accent
      bright: '#FED7D7',     // Light version of accent
      deep: '#C53030',       // Dark version of accent
    },
    neutral: {
      // Neutral scale for backgrounds and text on dark backgrounds
      lightest: '#F7FAFC',   // Very light gray
      light: '#EDF2F7',      // Light gray
      medium: '#CBD5E0',     // Medium gray
      dark: '#4A5568',       // Dark gray
    },
  },
  shadows: {
    // Box shadows and overlays used throughout the app
    light: 'rgba(0, 0, 0, 0.05)',      // Subtle shadows
    medium: 'rgba(0, 0, 0, 0.1)',      // Medium shadows
    strong: 'rgba(0, 0, 0, 0.15)',     // Strong shadows
    dark: 'rgba(0, 0, 0, 0.2)',        // Dark shadows
    darker: 'rgba(0, 0, 0, 0.3)',      // Darker shadows
    overlay: {
      // Overlay backgrounds for modals and hero sections
      light: 'rgba(0, 0, 0, 0.3)',     // Light overlay
      medium: 'rgba(0, 0, 0, 0.4)',    // Medium overlay
      dark: 'rgba(0, 0, 0, 0.6)',      // Dark overlay
      darkest: 'rgba(0, 0, 0, 0.7)',   // Darkest overlay
    },
    // Special cases
    navBackground: 'rgba(255, 255, 255, 0.95)', // Transparent nav background
    accentShadow: 'rgba(229, 62, 62, 0.15)',    // Transparent accent shadow
  },
  typography: {
    primary: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      heroTitle: {
        fontSize: 'clamp(3rem, 10vw, 6rem)',
        fontWeight: '700',
        lineHeight: '1.1',
      },
      heading: {
        fontSize: 'clamp(2rem, 5vw, 3rem)',
        fontWeight: '600',
        lineHeight: '1.2',
      },
      largeHeading: {
        fontSize: 'clamp(3.5rem, 8vw, 4.5rem)',
        fontWeight: '700',
        lineHeight: '1.1',
      },
      mediumHeading: {
        fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
        fontWeight: '600',
        lineHeight: '1.2',
      },
      smallHeading: {
        fontSize: 'clamp(2rem, 4vw, 2.8rem)',
        fontWeight: '600',
        lineHeight: '1.2',
      },
    },
    secondary: {
      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      body: {
        fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
        fontWeight: '400',
        lineHeight: '1.6',
      },
      button: {
        fontSize: '0.9rem',
        fontWeight: '500',
      },
      navigation: {
        fontSize: '0.9rem',
        fontWeight: '400',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      },
    },
    button: {
      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      fontSize: '0.875rem',
      fontWeight: '600',
      letterSpacing: '0.025em',
      textTransform: 'uppercase',
    },
    navigation: {
      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      fontSize: '0.875rem',
      fontWeight: '500',
      letterSpacing: '0.025em',
      textTransform: 'uppercase',
    },
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '32px',
    xl: '64px',
    xxl: '128px',
    layoutGutter: 'clamp(1rem, 5vw, 3rem)',
    sectionPadding: {
      vertical: 'clamp(4rem, 10vh, 8rem)',
      horizontal: 'clamp(1rem, 5vw, 3rem)',
    },
    section: {
      standard: '120px 0',
      mobile: '60px 0',
    },
    grid: {
      standard: '120px',
      mobile: '60px',
    },
    navigation: {
      height: '70px',
      mobileHeight: '60px',
    },
  },
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1440px',
  },
  transitions: {
    default: 'all 0.3s ease',
    slow: 'all 0.6s ease',
  },
  animations: {
    // Animation durations for scroll animations
    fast: 0.6,        // Fast animations
    medium: 0.8,      // Medium animations  
    slow: 1.0,        // Slow animations
    // Stagger delays
    staggerFast: 0.2,    // Quick stagger between elements
    staggerMedium: 0.3,  // Medium stagger
    staggerSlow: 0.4,    // Slow stagger
    // Movement distances
    moveSmall: 20,    // Small y movement
    moveMedium: 30,   // Medium y movement
    moveLarge: 50,    // Large y movement
    // Scale values
    scaleSmall: 0.9,  // Small scale effect
    scaleMedium: 0.8, // Medium scale effect
    // Gallery timing
    imageRotationInterval: 2500, // milliseconds between image changes
  },
  // Reusable CSS mixins
  mixins: {
    // Hover underline effect used across navigation and movement selectors
    hoverUnderline: (color = 'currentColor', thickness = '1px') => `
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: ${thickness};
        background-color: ${color};
        transition: width 0.3s ease;
      }
      
      &:hover::after {
        width: 100%;
      }
    `,
  },
};